// Contact Entries Component for CRM Entities
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import {
  Plus,
  MapPin,
  Mail,
  Phone,
  Pencil,
  Trash2,
  Star,
  Loader2,
  PhoneCall,
  MessageSquare,
  CheckCircle2,
} from 'lucide-react';
import { ContactDetailDialog, CommunicationDialog } from './ActivityDialogs';
import { userAPI } from '../../services/api';
import { useConfirm } from '../../components/common/ConfirmDialog';

const BACKEND_URL = window.location.origin;

const addressTypes = [
  { value: 'office', label: 'Office' },
  { value: 'billing', label: 'Billing' },
  { value: 'mailing', label: 'Mailing' },
  { value: 'other', label: 'Other' },
];

const emailTypes = [
  { value: 'office', label: 'Office' },
  { value: 'billing', label: 'Billing' },
  { value: 'other', label: 'Other' },
];

const phoneTypes = [
  { value: 'office', label: 'Office' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'fax', label: 'Fax' },
  { value: 'other', label: 'Other' },
];

const ContactEntries = ({ crmEntityId, tenantId, readOnly = false, canEditAccount = true }) => {
  const [entries, setEntries] = useState([]);
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Communication dialog state
  const [commDialogOpen, setCommDialogOpen] = useState(false);
  const [commInitialData, setCommInitialData] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    contact_type: 'email',
    // Address
    address_type: 'office',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    // Email
    email_type: 'office',
    email_address: '',
    // Phone
    phone_type: 'office',
    phone_number: '',
    // Common
    nickname: '',
    is_primary: false,
    notes: '',
  });

  useEffect(() => {
    if (crmEntityId) {
      fetchEntries();
      userAPI.list().then(r => setTenantUsers(r.data || [])).catch(() => {});
    }
  }, [crmEntityId]);

  const fetchEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/contact-entries?crm_entity_id=${crmEntityId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      }
    } catch (error) {
      console.error('Failed to load contact entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      contact_type: 'email',
      address_type: 'office',
      street: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'USA',
      email_type: 'office',
      email_address: '',
      phone_type: 'office',
      phone_number: '',
      nickname: '',
      is_primary: false,
      notes: '',
    });
    setEditingEntry(null);
  };

  const openAddDialog = (type) => {
    resetForm();
    setFormData((prev) => ({ ...prev, contact_type: type }));
    setDialogOpen(true);
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData(entry);
    setDialogOpen(true);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const method = editingEntry ? 'PUT' : 'POST';
      const url = editingEntry
        ? `${BACKEND_URL}/api/contact-entries/${editingEntry.id}`
        : `${BACKEND_URL}/api/contact-entries`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          crm_entity_id: crmEntityId,
        }),
      });

      if (response.ok) {
        toast.success(editingEntry ? 'Contact updated' : 'Contact added');
        setDialogOpen(false);
        resetForm();
        fetchEntries();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save contact entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!await confirm({ title: 'Delete this contact entry?', destructive: true })) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/contact-entries/${entryId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Contact deleted');
        fetchEntries();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Handler to open communication dialog with pre-filled data
  const handleCommunicationAction = (contactEntry, method) => {
    let methodType = '';
    let description = '';
    
    if (method === 'email') {
      methodType = 'email_direct';
      description = `Email to: ${contactEntry.email_address}`;
    } else if (method === 'call') {
      methodType = 'telephone';
      description = `Phone call to: ${contactEntry.phone_number}`;
    } else if (method === 'text') {
      methodType = 'text';
      description = `Text message to: ${contactEntry.phone_number}`;
    }
    
    setCommInitialData({
      communication_method: methodType,
      activity_name: description,
      description: '',
      contact_entry_id: contactEntry.id,
    });
    
    setCommDialogOpen(true);
  };

  const addresses = entries.filter((e) => e.contact_type === 'address');
  const emails = entries.filter((e) => e.contact_type === 'email');
  const phones = entries.filter((e) => e.contact_type === 'phone');

  const renderContactTypeIcon = (type) => {
    switch (type) {
      case 'address':
        return <MapPin className="w-4 h-4" />;
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'phone':
        return <Phone className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="contact-entries">
      {/* Addresses */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Addresses
          </CardTitle>
          {!readOnly && (
            <Button variant="ghost" size="sm" onClick={() => openAddDialog('address')}>
              <Plus className="w-4 h-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {addresses.length === 0 ? (
            <p className="text-sm text-muted-foreground">No addresses</p>
          ) : (
            <div className="space-y-3">
              {addresses.map((addr) => (
                <div
                  key={addr.id}
                  className="group relative border border-border hover:border-primary/50 hover:bg-accent/50 transition-colors cursor-pointer"
                >
                  <div 
                    className="flex items-start justify-between p-3"
                    onClick={async () => {
                      setSelectedEntry(addr);
                      setDetailDialogOpen(true);
                    }}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">
                          {addr.address_type}
                        </Badge>
                        {addr.is_primary && (
                          <CheckCircle2 className="w-3 h-3 text-green-600" />
                        )}
                        {addr.nickname && (
                          <span className="text-xs text-muted-foreground">({addr.nickname})</span>
                        )}
                      </div>
                      <p className="text-sm">{addr.street}</p>
                      <p className="text-sm text-muted-foreground">
                        {addr.city}, {addr.state} {addr.zip_code}
                      </p>
                      {addr.country !== 'USA' && (
                        <p className="text-sm text-muted-foreground">{addr.country}</p>
                      )}
                    </div>
                  </div>
                  {/* Action Buttons */}
                  <div className="absolute top-2 right-2 flex gap-1">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${addr.street}, ${addr.city}, ${addr.state} ${addr.zip_code}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="p-1.5 rounded hover:bg-primary/10 transition-colors"
                      title="View on Map"
                    >
                      <MapPin className="w-4 h-4 text-primary" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Emails */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4" /> Emails
          </CardTitle>
          {!readOnly && (
            <Button variant="ghost" size="sm" onClick={() => openAddDialog('email')}>
              <Plus className="w-4 h-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {emails.length === 0 ? (
            <p className="text-sm text-muted-foreground">No emails</p>
          ) : (
            <div className="space-y-2">
              {emails.map((email) => (
                <div
                  key={email.id}
                  className="group relative border border-border hover:border-primary/50 hover:bg-accent/50 transition-colors cursor-pointer"
                >
                  <div
                    className="flex items-center justify-between p-2"
                    onClick={async () => {
                      setSelectedEntry(email);
                      setDetailDialogOpen(true);
                    }}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <Badge variant="outline" className="text-xs">
                        {email.email_type}
                      </Badge>
                      <span className="text-sm">{email.email_address}</span>
                      {email.is_primary && (
                        <CheckCircle2 className="w-3 h-3 text-green-600" />
                      )}
                      {email.nickname && (
                        <span className="text-xs text-muted-foreground">({email.nickname})</span>
                      )}
                    </div>
                  </div>
                  {/* Action Button */}
                  <div className="absolute top-2 right-2 flex gap-1">
                    {/* Native mailto: link for mobile */}
                    <a
                      href={`mailto:${email.email_address}`}
                      onClick={(e) => e.stopPropagation()}
                      className="p-1.5 rounded hover:bg-primary/10 transition-colors"
                      title={`Email ${email.email_address}`}
                    >
                      <Mail className="w-4 h-4 text-primary" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Phones */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Phone className="w-4 h-4" /> Phone Numbers
          </CardTitle>
          {!readOnly && (
            <Button variant="ghost" size="sm" onClick={() => openAddDialog('phone')}>
              <Plus className="w-4 h-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {phones.length === 0 ? (
            <p className="text-sm text-muted-foreground">No phone numbers</p>
          ) : (
            <div className="space-y-2">
              {phones.map((phone) => (
                <div
                  key={phone.id}
                  className="group relative border border-border hover:border-primary/50 hover:bg-accent/50 transition-colors cursor-pointer"
                >
                  <div
                    className="flex items-center justify-between p-2"
                    onClick={async () => {
                      setSelectedEntry(phone);
                      setDetailDialogOpen(true);
                    }}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <Badge variant="outline" className="text-xs">
                        {phone.phone_type}
                      </Badge>
                      <span className="text-sm font-mono">{phone.phone_number}</span>
                      {phone.is_primary && (
                        <CheckCircle2 className="w-3 h-3 text-green-600" />
                      )}
                      {phone.nickname && (
                        <span className="text-xs text-muted-foreground">({phone.nickname})</span>
                      )}
                    </div>
                  </div>
                  {/* Action Buttons */}
                  <div className="absolute top-2 right-2 flex gap-1">
                    {/* Native tel: link — triggers phone dialer on mobile */}
                    <a
                      href={`tel:${(phone.phone_number || '').replace(/\s/g, '')}`}
                      onClick={(e) => e.stopPropagation()}
                      className="p-1.5 rounded hover:bg-primary/10 transition-colors"
                      title={`Call ${phone.phone_number}`}
                    >
                      <PhoneCall className="w-4 h-4 text-primary" />
                    </a>
                    <a
                      href={`sms:${(phone.phone_number || '').replace(/\s/g, '')}`}
                      onClick={(e) => e.stopPropagation()}
                      className="p-1.5 rounded hover:bg-primary/10 transition-colors"
                      title={`Text ${phone.phone_number}`}
                    >
                      <MessageSquare className="w-4 h-4 text-primary" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? 'Edit' : 'Add'} {formData.contact_type}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {formData.contact_type === 'address' && (
              <>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={formData.address_type}
                    onValueChange={(v) => handleChange('address_type', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {addressTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Street Address</Label>
                  <Input
                    value={formData.street}
                    onChange={(e) => handleChange('street', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={formData.state}
                      onChange={(e) => handleChange('state', e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>ZIP Code</Label>
                    <Input
                      value={formData.zip_code}
                      onChange={(e) => handleChange('zip_code', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input
                      value={formData.country}
                      onChange={(e) => handleChange('country', e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            {formData.contact_type === 'email' && (
              <>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={formData.email_type}
                    onValueChange={(v) => handleChange('email_type', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {emailTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={formData.email_address}
                    onChange={(e) => handleChange('email_address', e.target.value)}
                  />
                </div>
              </>
            )}

            {formData.contact_type === 'phone' && (
              <>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={formData.phone_type}
                    onValueChange={(v) => handleChange('phone_type', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {phoneTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input
                    value={formData.phone_number}
                    onChange={(e) => handleChange('phone_number', e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Nickname (optional)</Label>
              <Input
                value={formData.nickname}
                onChange={(e) => handleChange('nickname', e.target.value)}
                placeholder="e.g., Farm Office, Personal Cell"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_primary"
                checked={formData.is_primary}
                onCheckedChange={(checked) => handleChange('is_primary', checked)}
              />
              <Label htmlFor="is_primary">Primary contact</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Communication Dialog */}
      <CommunicationDialog
        open={commDialogOpen}
        onOpenChange={setCommDialogOpen}
        accountId={crmEntityId}
        contactEntries={entries}
        users={tenantUsers}
        onSuccess={() => {
          setCommDialogOpen(false);
          toast.success('Communication logged');
        }}
        initialData={commInitialData}
      />

      {/* Contact Detail Dialog */}
      <ContactDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        contactEntry={selectedEntry}
        onSuccess={() => {
          fetchEntries();
          setDetailDialogOpen(false);
        }}
        canEditAccount={canEditAccount}
      />
    </div>
  );
};

export default ContactEntries;
